import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Caption, Card, FormLayout, Select, Stack, TextField, TextStyle } from "@shopify/polaris";

import { useMutation, useQuery } from "@apollo/react-hooks";

// import constant
import constant from "lib/constant/constant";

// import helper
import { baseHelper, errorHelper } from "lib/helpers";

import { SkeletonCard, Toast } from "lib/components";

// import hoc
import { withErrorBoundary } from "lib/hoc";

// import context
import { PrivateContext } from "lib/context/privateContext";
import { ProductContext } from "app/product/modules/generic/context";

import { GET_METAFIELD_SETTING, GET_PRODUCT } from "app/product/apollo/queries";
import { ADD_PRODUCT_METAFIELD } from "app/product/apollo/mutations";

const EditMetafield = (props) => {
  const { cms, history, match } = useContext(PrivateContext);
  const { handleTabChange, setDescription, setTitle, setBanner } = useContext(ProductContext);
  const { tabObj = {} } = props;
  const { islastTab = false } = tabObj || {};
  const [disabledButton, setDisabledButton] = useState(true);
  const [isValid, setIsValid] = useState(true);
  const [message, setMessage] = useState("");
  const [selectedValue, setSelectedValue] = useState("kg");
  const [updatedAt, setUpdatedAt] = useState("");
  const [value, setValue] = useState({});
  let defaultUnit;

  const { loading: productLoading, data: productData } = useQuery(GET_PRODUCT, {
    variables: { input: { id: match.params.id } },
  });

  const { loading: catalogLoading, data: metaFieldData } = useQuery(GET_METAFIELD_SETTING);

  const [addProductMetafield, { loading: updateTagSettingLoading }] = useMutation(ADD_PRODUCT_METAFIELD);

  // load product catalog data
  const metafieldSetting = baseHelper.getResponseData(metaFieldData, constant.gql.GET_METAFIELD_SETTING) || {};
  const { metafield: metafieldData = [] } = metafieldSetting;

  setTitle(cms("label.title.metafield"));
  setDescription(cms("label.addMetafield"));

  useEffect(() => {
    if (productData) {
      const productResponse = baseHelper.getResponseData(productData, constant.gql.GET_PRODUCT) || {};

      const dataToSend = {};
      const metaData = productResponse && productResponse.customMetafields;
      const updatedAtDate = productResponse && productResponse && productResponse.updatedAt;
      if (metaData) {
        metaData.forEach((data) => {
          if (data && data.value && data.value.unit) {
            setSelectedValue(data.value.unit);
          }
          dataToSend[data.key] = data.value;
        });
        setValue(dataToSend);
        setUpdatedAt(updatedAtDate);
      }
    }
  }, [constant.gql.GET_PRODUCT, productData]);

  const handleChange = useCallback(
    (fieldName, fieldValue) => {
      setValue({
        ...value,
        [fieldName]: fieldValue,
      });
      setDisabledButton(false);
    },
    [value]
  );

  const handleMeasurement = (fieldName, fieldValue) => {
    setDisabledButton(false);
    setSelectedValue(fieldValue);
    setValue((prevState) => ({
      ...prevState,
      [fieldName.key]: { value: (value && value[fieldName.key] && value[fieldName.key].value) || "", unit: fieldValue },
    }));
  };

  const typeOption = (data) => {
    const splitData = data[0].split(",");
    [defaultUnit] = splitData;
    return splitData.map((key) => ({ label: key, value: key }));
  };

  const selectDropdown = (field) => {
    return (
      <Select
        value={(value && value[field.key] && value[field.key].unit) || ""}
        options={typeOption(field && field.value)}
        onChange={(val) => {
          handleMeasurement(field, val);
        }}
      />
    );
  };

  const handleDropdown = (field) => {
    if (field.contentType === constant.MEASUREMENT) {
      return selectDropdown(field);
    }
    return null;
  };

  const handlePlaceHolder = (field) => {
    if ([constant.NUMBER, constant.MEASUREMENT].includes(field)) {
      return constant.ZERO_PRICE;
    }
    if (field === constant.INTEGER) {
      return constant.ZERO_PRICE_INT;
    }
    return null;
  };

  const handleValidateInput = (field, val) => {
    setIsValid(true);
    if ([constant.NUMBER, constant.MEASUREMENT].includes(field.contentType)) {
      const validate =
        field.contentType === constant.NUMBER
          ? baseHelper.validatePositiveNumericValues(val)
          : baseHelper.validatePositiveNumericValues(val && val.value);
      if (field.contentType === constant.NUMBER) {
        if (Number(val) > Number(field.value)) {
          setBanner({
            isOpen: true,
            status: constant.CRITICAL,
            title: cms("message.error.maxLength"),
          });
          setIsValid(false);
        }
      }
      return (validate && val) || "";
    }
    if (field.contentType === constant.STRING && val.length > field.value) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("message.error.maxLength"),
      });
      setIsValid(false);
    }
    return val;
  };

  const onFormSubmit = async () => {
    try {
      // convert input into key value pair
      const convertedData = Object.entries(value).map(([key, convertValue]) => ({ key, value: convertValue }));
      const formData = {
        productId: match.params.id,
        metafield: convertedData,
      };
      if (isValid) {
        const res = await addProductMetafield({
          variables: {
            input: formData,
          },
        });
        const resData = baseHelper.getResponseData(res.data, constant.gql.ADD_PRODUCT_METAFIELD);
        const resError = baseHelper.getResponseError(res.data, constant.gql.ADD_PRODUCT_METAFIELD);
        if (resError) {
          setBanner({
            status: constant.CRITICAL,
            isOpen: true,
            title: resError,
          });
        }
        if (resData) {
          setMessage(cms("message.success.metafield"));
          setTimeout(() => {
            history.push("/products");
          }, 1500);
        }
      }
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  if (catalogLoading || productLoading) {
    return <SkeletonCard />;
  }

  return (
    <>
      {/* {bannerStatus.isOpen && (
        <Layout.Section>
          <Banner
            isOpen={bannerStatus.isOpen}
            status={bannerStatus.status}
            title={bannerStatus.title}
            onDismiss={() => dismissBanner()}
          />
        </Layout.Section>
      )} */}
      <Card
        title={[
          cms("label.productMetafield"),
          updatedAt && (
            <TextStyle variation="subdued">
              <Caption>{`${baseHelper.lastUpdateDate(updatedAt)}`}</Caption>
            </TextStyle>
          ),
        ]}
      >
        <Card.Section>
          <p>{cms("label.caption.metafield")}</p>
          <br />
          <FormLayout>
            {metafieldData &&
              metafieldData.length &&
              metafieldData.map((metafield) => (
                <TextField
                  label={metafield.name}
                  placeholder={handlePlaceHolder(metafield.contentType)}
                  value={
                    metafield.contentType === constant.MEASUREMENT
                      ? (value[metafield.key] && value[metafield.key].value) || ""
                      : value[metafield.key]
                  }
                  onChange={(val) => {
                    // eslint-disable-next-line no-unused-expressions
                    metafield.contentType === constant.MEASUREMENT
                      ? handleChange(
                          metafield.key,
                          handleValidateInput(metafield, {
                            value: val,
                            unit: !selectedValue ? defaultUnit : selectedValue,
                          })
                        )
                      : handleChange(metafield.key, handleValidateInput(metafield, val));
                  }}
                  connectedRight={handleDropdown(metafield)}
                />
              ))}
          </FormLayout>
        </Card.Section>
      </Card>
      <br />
      <Stack>
        <Stack.Item fill>
          <Button onClick={() => handleTabChange(4)}>{cms("button.previous")}</Button>
        </Stack.Item>
        <Stack.Item>
          <Button
            primary
            loading={updateTagSettingLoading}
            disabled={disabledButton || !isValid}
            onClick={() => onFormSubmit()}
          >
            {islastTab
              ? cms("button.finish")
              : cms(`button.${islastTab ? constant.SUBMIT_SMALL : constant.SAVE_SMALL}`)}
          </Button>
        </Stack.Item>
      </Stack>
      <div className="toast">
        <Toast message={message} />
      </div>
    </>
  );
};

export default withErrorBoundary(EditMetafield);
